/**
 * ScaliqAgentsSection — "LinkedIn-agents (ScalIQ)" op de Orchestration-pagina.
 *
 * Toont de gekoppelde ScalIQ LinkedIn-accounts (Dominique/Jeremy) als agents
 * met live dagquota, plus de configuratie van de twee autonome agents:
 *  - Auto-push agent: leads in stage X automatisch naar campagne Y (dagcap)
 *  - Reply-agent: AI-conceptantwoord bij elke ScalIQ-reply (pending approval)
 * Instellingen → PATCH /integrations/scaliq {settings:{agents:{...}}}.
 */
import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import i18n from "i18next";
import {
    Rocket, Loader2, Linkedin, Zap, MessageSquareReply, Save,
} from "lucide-react";

const STAGES = ["new", "contacted", "engaged"];

export default function ScaliqAgentsSection({ canMutate }) {
    const [data, setData] = useState(null);
    const [form, setForm] = useState(null);
    const [campaigns, setCampaigns] = useState([]);
    const [saving, setSaving] = useState(false);

    const load = async () => {
        try {
            const r = await api.get("/scaliq/agents/status");
            setData(r.data);
            setForm(r.data.settings);
        } catch { setData({ configured: false }); return; }
        try {
            const c = await api.get("/scaliq/campaigns");
            setCampaigns((c.data.campaigns || []).filter((x) => x.status !== "CAMPAIGN_ARCHIVED"));
        } catch { /* campagnelijst faalde — sectie blijft bruikbaar */ }
    };
    useEffect(() => { load(); }, []);

    if (!data || !data.configured) return null; // geen ScalIQ-key → sectie verbergen

    const save = async () => {
        // Bevestiging bij activeren: de agent pusht daarna elke 30 min échte
        // leads naar een échte LinkedIn-campagne (irreversibel, kost credits).
        if (form.auto_push_enabled && form.auto_push_campaign_id) {
            const campaign = campaigns.find((c) => c.id === form.auto_push_campaign_id);
            const ok = window.confirm(i18n.t("scaliq.autopush_confirm", {
                stage: i18n.t(`pipeline_sales.stage_${form.auto_push_stage}`, form.auto_push_stage),
                name: campaign?.name || form.auto_push_campaign_id,
                cap: form.auto_push_daily_cap,
            }));
            if (!ok) return;
        }
        setSaving(true);
        try {
            const campaign = campaigns.find((c) => c.id === form.auto_push_campaign_id);
            await api.patch("/integrations/scaliq", {
                settings: { agents: { ...form, auto_push_campaign_name: campaign?.name || form.auto_push_campaign_name || "" } },
            });
            toast.success(i18n.t("scaliq.agents_saved"));
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || i18n.t("integr.save_failed")); }
        finally { setSaving(false); }
    };

    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    return (
        <section className="mt-10" data-testid="scaliq-agents-section">
            <div className="flex items-center gap-2 mb-1">
                <Rocket size={15} className="text-[#818cf8]" />
                <h3 className="text-sm font-medium text-white">{i18n.t("scaliq.agents_title")}</h3>
            </div>
            <p className="text-[11px] text-[var(--jr-text-3)] mb-4">{i18n.t("scaliq.agents_note")}</p>

            {/* LinkedIn-accounts als agents */}
            <div className="grid sm:grid-cols-2 gap-3 mb-4">
                {(data.accounts || []).map((a) => {
                    const today = a.today || {};
                    return (
                        <div key={a.id} data-testid={`scaliq-agent-account-${a.id}`}
                            className="rounded-xl border border-white/10 bg-[var(--jr-surface)] p-4">
                            <div className="flex items-center gap-3">
                                <span className="w-9 h-9 rounded-full bg-[#0a66c2]/20 grid place-items-center text-[#5eb0ef]">
                                    <Linkedin size={16} />
                                </span>
                                <div className="min-w-0">
                                    <div className="text-sm text-white font-medium truncate">{a.name}</div>
                                    <div className="flex items-center gap-1.5 text-[10px] text-[var(--jr-text-3)]">
                                        <span className={`w-1.5 h-1.5 rounded-full ${a.status === "connected" ? "bg-[#22c55e]" : "bg-[#ef4444]"}`} />
                                        {a.status === "connected" ? i18n.t("scaliq.acc_connected") : a.status}
                                        {a.hasSalesNavigator ? " · SalesNav" : ""}
                                        {data.default_account_id === a.id && ` · ${i18n.t("scaliq.default_badge")}`}
                                    </div>
                                </div>
                            </div>
                            <div className="mt-3 grid grid-cols-2 gap-2">
                                <QuotaBar label={i18n.t("scaliq.q_connections")} used={today.connectionRequestsSent ?? 0} max={today.connectionRequestLimit ?? 0} />
                                <QuotaBar label={i18n.t("scaliq.q_messages")} used={today.messagesSent ?? 0} max={today.messageLimit ?? 0} />
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Agent-configuratie */}
            {form && (
                <div className="grid lg:grid-cols-2 gap-3">
                    {/* Auto-push agent */}
                    <div className="rounded-xl border border-white/10 bg-[var(--jr-surface)] p-4" data-testid="scaliq-autopush-card">
                        <div className="flex items-center gap-2">
                            <Zap size={14} className="text-[#818cf8]" />
                            <span className="text-sm text-white font-medium flex-1">{i18n.t("scaliq.autopush_title")}</span>
                            <button onClick={() => canMutate && set("auto_push_enabled", !form.auto_push_enabled)}
                                data-testid="scaliq-autopush-toggle" disabled={!canMutate}
                                className={`w-9 h-5 rounded-full transition-colors relative ${form.auto_push_enabled ? "bg-[#6366f1]" : "bg-white/15"} disabled:opacity-50`}>
                                <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all ${form.auto_push_enabled ? "left-[18px]" : "left-0.5"}`} />
                            </button>
                        </div>
                        <p className="mt-1 text-[10px] text-[var(--jr-text-3)]">{i18n.t("scaliq.autopush_note")}</p>
                        <div className="mt-3 space-y-2.5">
                            <div>
                                <label className="text-[10px] font-mono uppercase tracking-[0.16em] text-[var(--jr-text-3)]">{i18n.t("scaliq.autopush_campaign")}</label>
                                <select value={form.auto_push_campaign_id} disabled={!canMutate}
                                    onChange={(e) => set("auto_push_campaign_id", e.target.value)}
                                    data-testid="scaliq-autopush-campaign"
                                    className="mt-1 w-full bg-[var(--jr-surface-2)] border border-white/10 rounded-lg px-2.5 py-2 text-xs text-white outline-none focus:border-[#6366f1]">
                                    <option value="">{i18n.t("scaliq.pick_a_campaign")}</option>
                                    {campaigns.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                                </select>
                            </div>
                            <div className="grid grid-cols-2 gap-2.5">
                                <div>
                                    <label className="text-[10px] font-mono uppercase tracking-[0.16em] text-[var(--jr-text-3)]">{i18n.t("scaliq.autopush_stage")}</label>
                                    <select value={form.auto_push_stage} disabled={!canMutate}
                                        onChange={(e) => set("auto_push_stage", e.target.value)}
                                        data-testid="scaliq-autopush-stage"
                                        className="mt-1 w-full bg-[var(--jr-surface-2)] border border-white/10 rounded-lg px-2.5 py-2 text-xs text-white outline-none focus:border-[#6366f1]">
                                        {STAGES.map((s) => <option key={s} value={s}>{i18n.t(`pipeline_sales.stage_${s}`, s)}</option>)}
                                    </select>
                                </div>
                                <div>
                                    <label className="text-[10px] font-mono uppercase tracking-[0.16em] text-[var(--jr-text-3)]">{i18n.t("scaliq.autopush_cap")}</label>
                                    <input type="number" min={1} max={50} value={form.auto_push_daily_cap} disabled={!canMutate}
                                        onChange={(e) => set("auto_push_daily_cap", e.target.value)}
                                        onBlur={(e) => set("auto_push_daily_cap", Math.max(1, Math.min(50, parseInt(e.target.value || "10", 10) || 10)))}
                                        data-testid="scaliq-autopush-cap"
                                        className="mt-1 w-full bg-[var(--jr-surface-2)] border border-white/10 rounded-lg px-2.5 py-2 text-xs text-white outline-none focus:border-[#6366f1]" />
                                </div>
                            </div>
                            <div className="text-[10px] text-[var(--jr-text-3)]">
                                {i18n.t("scaliq.autopush_today", { n: data.pushed_today || 0, cap: form.auto_push_daily_cap })}
                            </div>
                        </div>
                    </div>

                    {/* Reply-agent */}
                    <div className="rounded-xl border border-white/10 bg-[var(--jr-surface)] p-4 flex flex-col" data-testid="scaliq-replyagent-card">
                        <div className="flex items-center gap-2">
                            <MessageSquareReply size={14} className="text-[#818cf8]" />
                            <span className="text-sm text-white font-medium flex-1">{i18n.t("scaliq.replyagent_title")}</span>
                            <button onClick={() => canMutate && set("reply_agent_enabled", !form.reply_agent_enabled)}
                                data-testid="scaliq-replyagent-toggle" disabled={!canMutate}
                                className={`w-9 h-5 rounded-full transition-colors relative ${form.reply_agent_enabled ? "bg-[#6366f1]" : "bg-white/15"} disabled:opacity-50`}>
                                <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all ${form.reply_agent_enabled ? "left-[18px]" : "left-0.5"}`} />
                            </button>
                        </div>
                        <p className="mt-1 text-[10px] text-[var(--jr-text-3)]">{i18n.t("scaliq.replyagent_note")}</p>
                        <div className="mt-auto pt-3 text-[10px] text-[var(--jr-text-3)]">
                            {i18n.t("scaliq.replyagent_pending", { n: data.reply_drafts_pending || 0 })}
                        </div>
                    </div>
                </div>
            )}

            {canMutate && form && (
                <button onClick={save} disabled={saving} data-testid="scaliq-agents-save"
                    className="mt-3 inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#6366f1] text-white text-xs font-medium hover:bg-[#6366f1]/85 disabled:opacity-50">
                    {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />} {i18n.t("scaliq.agents_save_btn")}
                </button>
            )}
        </section>
    );
}

function QuotaBar({ label, used, max }) {
    const pct = max > 0 ? Math.min(100, Math.round((used / max) * 100)) : 0;
    return (
        <div>
            <div className="flex justify-between text-[9px] font-mono uppercase tracking-[0.12em] text-[var(--jr-text-3)]">
                <span>{label}</span><span>{used}/{max || "–"}</span>
            </div>
            <div className="mt-1 h-1.5 rounded-full bg-white/10 overflow-hidden">
                <div className="h-full rounded-full bg-[#6366f1]" style={{ width: `${pct}%` }} />
            </div>
        </div>
    );
}
